/**
 * Minimal single-file ZIP extractor for Cloudflare Workers (Story 19.2).
 *
 * Microsoft Advertising's Reporting Service v13 returns reports as ZIP archives
 * containing exactly one CSV file. Cloudflare Workers' built-in
 * `DecompressionStream("gzip" | "deflate" | "deflate-raw")` does NOT understand
 * ZIP container framing — it only handles the raw deflate payload inside.
 *
 * This module parses just enough of the ZIP format (PKZIP appnote 6.3.4) to
 * locate the single file's local header, read its compressed bytes, and
 * decompress them through `DecompressionStream("deflate-raw")`.
 *
 * Why no dependency:
 *   - `fflate` (~13 KB) and `jszip` (~150 KB) both do far more than we need.
 *   - The full implementation here is ~120 lines, easy to audit, zero deps,
 *     and aligns with the build-to-sell directive (a buyer's engineer can
 *     understand it from the code alone).
 *   - If we ever encounter a ZIP64 archive (>4 GB or >65k files), swap to
 *     `fflate` — the public signature `unzipSingleFileToText()` stays the same.
 *
 * Limitations (deliberate):
 *   - Single-file archives only (Microsoft always emits one CSV).
 *   - No password / encryption support (Microsoft does not encrypt).
 *   - No ZIP64 support (Microsoft caps reports at 4 GB; we cap fetch at 90
 *     days which is well below).
 */

const SIG_LOCAL_FILE_HEADER = 0x04034b50;
const SIG_CENTRAL_DIRECTORY = 0x02014b50;
const SIG_END_OF_CENTRAL_DIRECTORY = 0x06054b50;

const COMPRESSION_STORED = 0;
const COMPRESSION_DEFLATE = 8;

/**
 * Extracts the single file inside a ZIP archive and returns its bytes as a
 * UTF-8 string. Suitable for Microsoft Ads CSV reports.
 *
 * Errors:
 *   - "ZIP archive is empty" — buffer too small for an EOCD record
 *   - "Not a ZIP archive" — local-file-header signature missing
 *   - "Unsupported ZIP compression method N" — anything other than store/deflate
 *   - "ZIP archive contains no files" — central directory empty
 */
export async function unzipSingleFileToText(
  zipBytes: ArrayBuffer
): Promise<string> {
  const view = new DataView(zipBytes);
  const u8 = new Uint8Array(zipBytes);

  if (zipBytes.byteLength < 22) {
    throw new Error("ZIP archive is empty");
  }

  // Verify the local file header at offset 0. Microsoft's ZIPs always begin
  // with one. We deliberately do NOT scan the End Of Central Directory record
  // — a single-file ZIP is identical from local-header-first parsing, and the
  // EOCD scan is the source of most ZIP-parser CVEs.
  const sig = view.getUint32(0, true);
  if (sig !== SIG_LOCAL_FILE_HEADER) {
    throw new Error("Not a ZIP archive (missing local file header)");
  }

  // Local file header layout (PKZIP appnote 6.3.4 §4.3.7):
  //   0   4  Local file header signature
  //   4   2  Version needed to extract
  //   6   2  General purpose bit flag
  //   8   2  Compression method
  //  10   2  Last mod file time
  //  12   2  Last mod file date
  //  14   4  CRC-32
  //  18   4  Compressed size
  //  22   4  Uncompressed size
  //  26   2  File name length (n)
  //  28   2  Extra field length (m)
  //  30   n  File name
  // 30+n  m  Extra field
  // 30+n+m   File data (compressed)
  const compressionMethod = view.getUint16(8, true);
  const generalPurposeBitFlag = view.getUint16(6, true);
  let compressedSize = view.getUint32(18, true);
  const fileNameLength = view.getUint16(26, true);
  const extraFieldLength = view.getUint16(28, true);

  // If bit 3 of the general purpose flag is set, sizes/CRC live in a "data
  // descriptor" AFTER the compressed data. We then have to find the next
  // central-directory or local-file-header signature to know where the
  // compressed bytes end. Microsoft does not currently emit this form, but we
  // handle it for forward-compat.
  const hasDataDescriptor = (generalPurposeBitFlag & 0x0008) !== 0;
  const dataStart = 30 + fileNameLength + extraFieldLength;

  if (compressedSize === 0 && hasDataDescriptor) {
    // Scan forward for the next local file header / central directory.
    let cursor = dataStart;
    const end = u8.length - 4;
    let found = -1;
    while (cursor <= end) {
      const candidate = view.getUint32(cursor, true);
      if (
        candidate === SIG_CENTRAL_DIRECTORY ||
        candidate === SIG_LOCAL_FILE_HEADER ||
        candidate === SIG_END_OF_CENTRAL_DIRECTORY
      ) {
        found = cursor;
        break;
      }
      cursor++;
    }
    if (found === -1) {
      throw new Error("ZIP archive truncated (no central directory)");
    }
    // The data descriptor is the 16 bytes immediately preceding the next
    // signature: optional 4-byte signature + 4 CRC + 4 compressed + 4 uncompressed.
    // Subtract conservatively assuming the optional signature is present (most
    // common case — this is what `zip` writes by default).
    const descriptorWithSig = 16;
    const descriptorWithoutSig = 12;
    // Heuristic: peek 4 bytes back; if it equals 0x08074b50 (the optional
    // descriptor signature), use 16; otherwise use 12.
    const possibleDescSig = view.getUint32(found - descriptorWithSig, true);
    const descSize =
      possibleDescSig === 0x08074b50 ? descriptorWithSig : descriptorWithoutSig;
    compressedSize = found - descSize - dataStart;
  }

  if (compressedSize <= 0) {
    throw new Error("ZIP archive contains no files");
  }

  const compressed = u8.subarray(dataStart, dataStart + compressedSize);

  if (compressionMethod === COMPRESSION_STORED) {
    return new TextDecoder("utf-8").decode(compressed);
  }

  if (compressionMethod === COMPRESSION_DEFLATE) {
    // Workers expose a Web-standard DecompressionStream. "deflate-raw" decodes
    // the raw deflate stream that ZIP stores (no zlib wrapper).
    const decompressed = await rawInflate(compressed);
    return new TextDecoder("utf-8").decode(decompressed);
  }

  throw new Error(`Unsupported ZIP compression method ${compressionMethod}`);
}

/**
 * Decompresses a raw-deflate byte stream using the Web Streams API. Available
 * on Cloudflare Workers and modern browsers. We accept a Uint8Array view and
 * stream it through DecompressionStream, then concatenate the chunks.
 */
async function rawInflate(input: Uint8Array): Promise<Uint8Array> {
  // Copy into a fresh ArrayBuffer to ensure the underlying buffer is exactly
  // sized — some platforms reject a Uint8Array view that aliases a larger
  // buffer when constructing a Blob/Stream.
  const buffer = new ArrayBuffer(input.byteLength);
  new Uint8Array(buffer).set(input);

  // The spec uses ReadableStream<Uint8Array> on the input side and a
  // DecompressionStream("deflate-raw") to inflate to the output side.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const Decompressor = (globalThis as any).DecompressionStream;
  if (typeof Decompressor !== "function") {
    throw new Error(
      "DecompressionStream is not available in this runtime (required for ZIP unzip)"
    );
  }

  const stream = new Response(buffer).body;
  if (!stream) {
    throw new Error("Failed to construct ReadableStream for ZIP unzip");
  }

  const decompressed = stream.pipeThrough(new Decompressor("deflate-raw"));
  // The Response constructor in lib.dom.d.ts expects a stricter
  // ReadableStream<Uint8Array> while the spec allows ReadableStream<unknown>;
  // the runtime check in pipeThrough guarantees the cast is safe.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const out = await new Response(decompressed as any).arrayBuffer();
  return new Uint8Array(out);
}
